import { useEffect, useState } from 'react'
import { generateUniqueKey } from '../Utility'

// COMPONENTS
import Autosuggest from 'react-autosuggest'
import FloatingLabel from 'react-bootstrap/FloatingLabel'

const escapeRegexCharacters = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Dato un valore e un filtro, restituisce i suggerimenti che corrispondono al valore.
 * Se il valore è vuoto, restituisce un array vuoto.
 * Se filterValues falso, restituisce il filtro originale.
 *
 * @param {string} value - Il valore da cercare.
 * @param {array} filter - L'array di oggetti da cercare.
 * @param {boolean} filterValues - Se vero, le suggerimenti sono filtrate in base al valore.
 *
 * @returns {array} Le suggerimenti che corrispondono al valore.
 */
const getSuggestions = (value, filter = [], filterValues) => {
  if (filterValues === false) {
    return filter
  }

  const escapedValue = escapeRegexCharacters(value.trim())

  if (escapedValue === '') {
    return []
  }
  const regex = new RegExp('^.*' + escapedValue + '.*$', 'i')
  const suggestions = filter.filter((datas) => regex.test(datas.name))

  return suggestions
}

/**
 * AutoCompileCustom è un componente React che fornisce un campo di input con funzionalità
 * di suggerimento automatico. Rende un'etichetta, un campo di input e un elenco di suggerimenti
 * basati sull'input dell'utente. Il componente gestisce le modifiche al valore di input, la selezione
 * dei suggerimenti e l'ottenimento dei suggerimenti da un filtro fornito.
 *
 * Questa versione in particolare possiede la label flottante di bootstrap 5
 *
 * @see https://github.com/moroshko/react-autosuggest#readme
 *
 * @param {string} value - Il valore iniziale del campo di input.
 * @param {string} placeholder - Il testo segnaposto per il campo di input.
 * @param {function} handleInputAutocompile - Funzione di callback per gestire le modifiche
 * al valore di input.
 * @param {array} filter - L'array di oggetti suggerimento da cui filtrare.
 * @param {boolean} filterValues - Flag per determinare se filtrare i suggerimenti.
 * @param {string} id - L'identificatore univoco per il campo di input.
 * @param {string} label - Il testo dell'etichetta visualizzato sopra il campo di input.
 * @param {object} error - Oggetto di errore contenente lo stato di validazione e il messaggio.
 * @param {boolean} touched - Se il campo di input è stato interagito.
 *
 */
export const AutoCompileCustom = (props) => {
  const [value, setValue] = useState(props.value || '')
  const [suggestions, setSuggestions] = useState([])
  const [guid, setGuid] = useState()

  useEffect(() => {
    setGuid(generateUniqueKey('id'))
    if (document.getElementsByClassName('react-autosuggest__input').length === 1) {
      document.getElementsByClassName('react-autosuggest__input')[0].classList.add('form-control')
    }
  }, [])

  useEffect(() => {
    if (props.value !== value) {
      setValue(props.value || '')
    }
  }, [props.value])

  useEffect(() => {
    if (guid) {
      const formFloating = document.querySelector(`#${guid}`)
      if (formFloating) {
        if (value) {
          formFloating.classList.add('input-has-value')
        } else {
          formFloating.classList.remove('input-has-value')
        }
      }
    }
  }, [guid, value])

  /**
   * Funzione di callback per gestire le modifiche al valore di input.
   *
   * @param {Event} event - L'evento di modifica del campo di input.
   * @param {object} { newValue, method } - Oggetto contenente il nuovo valore e il metodo
   * che ha generato l'evento (ad esempio "click" o "enter").
   */
  const onChange = (event, { newValue }) => {
    setValue(newValue?.toUpperCase() || '')
    props.handleInputAutocompile(newValue, props.id, event.target.dataset.suggestionIndex)
  }

  /**
   * Funzione di callback per gestire la selezione di un suggerimento.
   * Se il suggerimento è stato selezionato tramite click, aggiorna il valore
   * del campo di input e richiama la funzione handleInputAutocompile con il nuovo
   * valore, l'indice del suggerimento selezionato e la chiave del suggerimento.
   * Se il suggerimento è stato selezionato tramite un metodo diverso dal click,
   * previene l'evento di default.
   *
   * @param {Event} event - L'evento di selezione del suggerimento.
   * @param {object} { suggestion, suggestionValue, method } - Oggetto contenente il suggerimento
   * selezionato, il suo valore e il metodo che ha generato l'evento
   * (ad esempio "click" o "enter").
   */
  const onSuggestionSelected = (event, { suggestion, suggestionValue, method }) => {
    if (method === 'click') {
      setValue(suggestionValue)
      props.handleInputAutocompile(
        suggestionValue,
        props.id,
        event.target.dataset.suggestionIndex,
        suggestion.key
      )
    } else {
      event.preventDefault()
    }
  }

  /**
   * Ritorna il valore del suggerimento da visualizzare.
   *
   * @param {object} suggestion - Il suggerimento selezionato.
   * @returns {string} Il valore del suggerimento.
   */
  const getSuggestionValue = (suggestion) => {
    return suggestion.name
  }

  /**
   * Funzione di callback per gestire la renderizzazione di un suggerimento.
   * Il suggerimento selezionato viene passato come argomento e deve essere
   * restituito come stringa.
   *
   * @param {object} suggestion - Il suggerimento selezionato.
   * @returns {string} Il suggerimento selezionato convertito in stringa.
   */
  const renderSuggestion = (suggestion) => {
    return suggestion.name
  }

  /**
   * Funzione di callback per gestire la richiesta di suggerimenti.
   * Chiamata quando l'utente modifica il valore del campo di input.
   * Recupera i suggerimenti per il valore corrente e li memorizza nello stato.
   *
   * @param {object} { value } - Oggetto contenente il valore del campo di input.
   */
  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value, props.filter, props.filterValues))
  }

  /**
   * Funzione di callback per gestire la cancellazione dei suggerimenti.
   * Chiamata quando l'utente cancella il valore del campo di input.
   * Cancella i suggerimenti per il valore corrente e li memorizza nello stato.
   */
  const onSuggestionsClearRequested = () => {
    setSuggestions([])
  }

  const inputProps = { value, onChange, disabled: props.disabled }
  const inputWrapperClass = `input-wrapper w-100 ${props.touched && props.error !== undefined ? 'has-error' : ''}`

  return (
    <div id={guid} className={inputWrapperClass}>
      <FloatingLabel title={props.label} id="floatingLabel" label={props.label}>
        <Autosuggest
          suggestions={suggestions}
          shouldRenderSuggestions={() => true}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          onSuggestionSelected={onSuggestionSelected}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
        />
      </FloatingLabel>
    </div>
  )
}
